import React, { useState, useRef, useLayoutEffect } from "react";

type TooltipPosition = "top" | "bottom" | "left" | "right";

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: TooltipPosition;
  className?: string;
  maxWidth?: number;
}

/**
 * CustomTooltip — instant hover tooltip with no browser delay.
 * Wraps any children and shows the tooltip text on hover.
 * Auto-flips if too close to screen edge.
 */
const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  position = "top",
  className = "",
  maxWidth = 260,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const show = () => {
    setCoords(null);
    setVisible(true);
  };
  const hide = () => {
    setVisible(false);
    setCoords(null);
  };

  useLayoutEffect(() => {
    if (!visible || !wrapperRef.current || !tooltipRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;
    const GAP = 6;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = rect.top - th - GAP;
        left = rect.left + rect.width / 2 - tw / 2;
        break;
      case "bottom":
        top = rect.bottom + GAP;
        left = rect.left + rect.width / 2 - tw / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2 - th / 2;
        left = rect.left - tw - GAP;
        break;
      case "right":
        top = rect.top + rect.height / 2 - th / 2;
        left = rect.right + GAP;
        break;
    }

    // Clamp to viewport
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    if (left < 4) left = 4;
    if (left + tw > vpW - 4) left = vpW - tw - 4;
    if (top < 4) top = 4;
    if (top + th > vpH - 4) top = rect.top - th - GAP;

    setCoords({ top, left });
  }, [visible, position]);

  if (!text) return <>{children}</>;

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {visible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: "fixed",
            top: coords ? coords.top : 0,
            left: coords ? coords.left : 0,
            opacity: coords ? 1 : 0,
            visibility: coords ? "visible" : "hidden",
            maxWidth,
            zIndex: 99999,
            pointerEvents: "none",
            transition: "none",
          }}
          className="bg-[#131327] text-white text-[11px] font-semibold px-2.5 py-1.5 rounded-lg shadow-xl whitespace-pre-wrap break-words leading-snug animate-in fade-in duration-75"
        >
          {text}
          {/* Arrow */}
          <span
            className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0"
            style={{
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #131327",
            }}
          />
        </div>
      )}
    </span>
  );
};

export default Tooltip;
